import React, { useMemo } from 'react';

function ConsensusDisplay({ consensusHistory, selectedMonth, onMonthChange, consensusData }) {
  // Support both new (consensusHistory) and old (consensusData) formats for backward compatibility
  const isHistoryFormat = !!consensusHistory;
  
  // Generate available date options from consensusHistory
  const availableDates = useMemo(() => {
    if (!consensusHistory || (!consensusHistory.ratings && !consensusHistory.price_targets)) {
      return [];
    }
    
    const dateSet = new Set();
    
    // Extract dates from ratings
    if (consensusHistory.ratings && Array.isArray(consensusHistory.ratings)) {
      consensusHistory.ratings.forEach(rating => {
        if (rating.fetched_at) {
          const date = new Date(rating.fetched_at);
          const key = `${date.getFullYear()}-${date.getMonth()}`;
          dateSet.add(key);
        }
      });
    }
    
    // Extract dates from price targets
    if (consensusHistory.price_targets && Array.isArray(consensusHistory.price_targets)) {
      consensusHistory.price_targets.forEach(target => {
        if (target.fetched_at) {
          const date = new Date(target.fetched_at);
          const key = `${date.getFullYear()}-${date.getMonth()}`;
          dateSet.add(key);
        }
      });
    }
    
    // Convert to formatted date options and sort descending
    // Note: month in key is 0-11 (JavaScript month), we store it as 1-12 (calendar month)
    const dateOptions = Array.from(dateSet).map(key => {
      const [year, monthIndex] = key.split('-').map(Number);
      const month = monthIndex + 1; // Convert 0-11 to 1-12
      const date = new Date(year, monthIndex, 1);
      const label = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
      return { year, month, label, key };
    }).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
    
    return dateOptions;
  }, [consensusHistory]);
  
  // Create a stable key for selectedMonth to ensure useMemo detects changes
  const selectedMonthKey = selectedMonth === null 
    ? 'latest' 
    : `${selectedMonth.year}-${selectedMonth.month}`;
  
  // Filter data based on selectedMonth or use latest
  const { rating, priceTarget } = useMemo(() => {
    if (!isHistoryFormat) {
      // Legacy format: use consensusData directly
      return {
        rating: consensusData?.rating || null,
        priceTarget: consensusData?.price_target || null
      };
    }
    
    if (!consensusHistory) {
      return { rating: null, priceTarget: null };
    }
    
    let selectedRating = null;
    let selectedPriceTarget = null;
    
    // Use first available date (most recent) if no month selected
    let effectiveMonth = selectedMonth;
    if (effectiveMonth === null && availableDates.length > 0) {
      effectiveMonth = { year: availableDates[0].year, month: availableDates[0].month };
    }
    
    if (effectiveMonth === null) {
      // No dates available: use first item from each array (already sorted descending)
      selectedRating = consensusHistory.ratings?.[0] || null;
      selectedPriceTarget = consensusHistory.price_targets?.[0] || null;
    } else {
      // Filter by month: find latest entry within selected month
      const { year, month } = effectiveMonth;
      const monthIndex = month - 1; // Convert 1-12 to 0-11 for JavaScript date comparison
      
      // Find rating for selected month (arrays are already sorted descending)
      if (consensusHistory.ratings && Array.isArray(consensusHistory.ratings)) {
        selectedRating = consensusHistory.ratings.find(r => {
          if (!r.fetched_at) return false;
          const date = new Date(r.fetched_at);
          // Validate date before comparison
          if (isNaN(date.getTime())) return false;
          return date.getFullYear() === year && date.getMonth() === monthIndex;
        }) || null;
      }
      
      // Find price target for selected month
      if (consensusHistory.price_targets && Array.isArray(consensusHistory.price_targets)) {
        selectedPriceTarget = consensusHistory.price_targets.find(t => {
          if (!t.fetched_at) return false;
          const date = new Date(t.fetched_at);
          // Validate date before comparison
          if (isNaN(date.getTime())) return false;
          return date.getFullYear() === year && date.getMonth() === monthIndex;
        }) || null;
      }
    }
    
    return { rating: selectedRating, priceTarget: selectedPriceTarget };
  }, [consensusHistory, selectedMonthKey, selectedMonth, isHistoryFormat, consensusData]);
  
  // Check if we have any data to display
  if (!rating && !priceTarget) {
    if (isHistoryFormat && selectedMonth !== null && availableDates.length > 0) {
      return (
        <div className="consensus-section">
          <p className="no-data-message">No consensus data available for {selectedMonth.label || `${selectedMonth.month}/${selectedMonth.year}`}</p>
        </div>
      );
    }
    return null;
  }

  // Helper function to get rating badge class
  const getRatingBadgeClass = (ratingText) => {
    if (!ratingText) return '';
    const ratingLower = ratingText.toLowerCase();
    if (ratingLower.includes('strong buy')) {
      return 'rating-strong-buy';
    } else if (ratingLower.includes('buy') || ratingLower.includes('moderate buy')) {
      return 'rating-buy';
    } else if (ratingLower.includes('hold')) {
      return 'rating-hold';
    } else if (ratingLower.includes('sell') || ratingLower.includes('strong sell')) {
      return 'rating-sell';
    }
    return 'rating-neutral';
  };

  const handleDateChange = (event) => {
    if (!onMonthChange) return;
    
    const value = event.target.value;
    const [year, month] = value.split('-').map(Number);
    onMonthChange({ year, month });
  };
  
  // Default to first available date (most recent) if no month selected
  const selectedValue = selectedMonth === null && availableDates.length > 0
    ? `${availableDates[0].year}-${availableDates[0].month}`
    : selectedMonth !== null
    ? `${selectedMonth.year}-${selectedMonth.month}`
    : '';

  return (
    <div className="consensus-section">
      {/* Date Selector */}
      {isHistoryFormat && availableDates.length > 0 && (
        <div className="consensus-date-selector" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <select
            id="consensus-date-select"
            value={selectedValue}
            onChange={handleDateChange}
            style={{ padding: '0.5rem', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            {availableDates.map(date => (
              <option key={date.key} value={`${date.year}-${date.month}`}>
                {date.label}
              </option>
            ))}
          </select>
        </div>
      )}
      
      <div className="consensus-grid">
        {/* Ratings Section */}
        <div className="consensus-rating-card">
          <h3 className="consensus-card-title">Ratings</h3>
          {rating ? (
            <>
              <div className="rating-breakdown">
                {rating.consensus_rating && (
                  <div className="rating-breakdown-item">
                    <span className="rating-label">OVERALL:</span>
                    <span className={`rating-value ${getRatingBadgeClass(rating.consensus_rating)}`}>
                      {rating.consensus_rating}
                    </span>
                  </div>
                )}
                {rating.strong_buy_count > 0 && (
                  <div className="rating-breakdown-item">
                    <span className="rating-label">STRONG BUY:</span>
                    <span className="rating-value rating-strong-buy">{rating.strong_buy_count}</span>
                  </div>
                )}
                {rating.buy_count > 0 && (
                  <div className="rating-breakdown-item">
                    <span className="rating-label">BUY:</span>
                    <span className="rating-value rating-buy">{rating.buy_count}</span>
                  </div>
                )}
                {rating.hold_count > 0 && (
                  <div className="rating-breakdown-item">
                    <span className="rating-label">HOLD:</span>
                    <span className="rating-value rating-hold">{rating.hold_count}</span>
                  </div>
                )}
                {rating.sell_count > 0 && (
                  <div className="rating-breakdown-item">
                    <span className="rating-label">SELL:</span>
                    <span className="rating-value rating-sell">{rating.sell_count}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="no-data-message">No rating data available</p>
          )}
        </div>

        {/* Price Target Section */}
        <div className="consensus-price-target-card">
          <h3 className="consensus-card-title">Price Targets</h3>
          {priceTarget ? (
            <>
              <div className="price-target-details">
                <div className="price-target-item price-target-item-average">
                  <span className="price-target-detail-label">AVERAGE:</span>
                  <span className="price-target-detail-value price-target-average-value">${priceTarget.average_target.toFixed(2)}</span>
                </div>
                <div className="price-target-item">
                  <span className="price-target-detail-label">HIGH:</span>
                  <span className="price-target-detail-value price-target-high-value">${priceTarget.highest_target.toFixed(2)}</span>
                </div>
                <div className="price-target-item">
                  <span className="price-target-detail-label">LOW:</span>
                  <span className="price-target-detail-value price-target-low-value">${priceTarget.lowest_target.toFixed(2)}</span>
                </div>
                {priceTarget.forecasted_upside !== null && priceTarget.forecasted_upside !== undefined && (
                  <div className="price-target-item">
                    <span className="price-target-detail-label">UPSIDE:</span>
                    <span className={`upside-indicator ${priceTarget.forecasted_upside >= 0 ? 'upside-positive' : 'upside-negative'}`}>
                      {priceTarget.forecasted_upside >= 0 ? '+' : ''}{priceTarget.forecasted_upside.toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="no-data-message">No price target data available</p>
          )}
        </div>
      </div>
      {/* Footer with analyst count and update date */}
      {((rating && rating.fetched_at) || (priceTarget && priceTarget.fetched_at)) && (
        <div className="consensus-section-footer">
          {(() => {
            const ratingsCount = rating?.total_analysts || 0;
            const targetsCount = priceTarget?.analyst_count || 0;
            let analystText = '';
            if (ratingsCount === targetsCount && ratingsCount > 0) {
              analystText = `${ratingsCount} Analysts • `;
            } else if (ratingsCount > 0 && targetsCount > 0) {
              analystText = `${ratingsCount} / ${targetsCount} Analysts • `;
            } else if (ratingsCount > 0) {
              analystText = `${ratingsCount} Analysts • `;
            } else if (targetsCount > 0) {
              analystText = `${targetsCount} Analysts • `;
            }
            
            // Get the most recent update date
            const ratingDate = rating?.fetched_at ? new Date(rating.fetched_at) : null;
            const targetDate = priceTarget?.fetched_at ? new Date(priceTarget.fetched_at) : null;
            let updateDate = null;
            if (ratingDate && targetDate) {
              updateDate = ratingDate > targetDate ? ratingDate : targetDate;
            } else if (ratingDate) {
              updateDate = ratingDate;
            } else if (targetDate) {
              updateDate = targetDate;
            }
            
            return (
              <>
                {analystText && <span>{analystText}</span>}
                {updateDate && <span>Updated {updateDate.toLocaleDateString()}</span>}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default ConsensusDisplay;


